"use client";

import React, { createContext, useContext, useState, useCallback } from "react";

export type ToastPosition = "top-center" | "bottom-center" | "center";
export type ToastVariant = "default" | "success" | "error" | "warning" | "info" | "loading";

export interface Toast {
  id: string;
  message: string;
  variant?: ToastVariant;
  position?: ToastPosition;
  duration?: number;
  isPromise?: boolean;
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (
    message: string,
    options?: {
      variant?: ToastVariant;
      position?: ToastPosition;
      duration?: number;
    }
  ) => void;
  promiseToast: <T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: any) => string);
    }
  ) => Promise<T>;
  dismissToast: (id: string) => void;
  updateToast: (id: string, updates: Partial<Toast>) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback(
    (
      message: string,
      options?: {
        variant?: ToastVariant;
        position?: ToastPosition;
        duration?: number;
      }
    ) => {
      const id = Math.random().toString(36).substring(2, 9);
      const newToast: Toast = {
        id,
        message,
        variant: options?.variant || "default",
        position: options?.position || "top-center",
        duration: options?.duration || 3000,
        isPromise: false,
      };

      setToasts((prev) => [...prev, newToast]);

      if (options?.variant !== "loading") {
        setTimeout(() => {
          dismissToast(id);
        }, newToast.duration);
      }
    },
    []
  );

  const updateToast = useCallback((id: string, updates: Partial<Toast>) => {
    setToasts((prev) =>
      prev.map((toast) =>
        toast.id === id ? { ...toast, ...updates } : toast
      )
    );

    if (updates.variant !== "loading" && updates.duration) {
      setTimeout(() => {
        dismissToast(id);
      }, updates.duration);
    }
  }, []);

  const promiseToast = useCallback(
    async <T,>(
      promise: Promise<T>,
      messages: {
        loading: string;
        success: string | ((data: T) => string);
        error: string | ((error: any) => string);
      }
    ): Promise<T> => {
      const id = Math.random().toString(36).substring(2, 9);
      const loadingToast: Toast = {
        id,
        message: messages.loading,
        variant: "loading",
        position: "center",
        isPromise: true,
      };

      setToasts((prev) => [...prev, loadingToast]);

      try {
        const data = await promise;
        const successMessage =
          typeof messages.success === "function"
            ? messages.success(data)
            : messages.success;

        updateToast(id, {
          message: successMessage,
          variant: "success",
          duration: 3000,
        });

        return data;
      } catch (error) {
        const errorMessage =
          typeof messages.error === "function"
            ? messages.error(error)
            : messages.error;

        updateToast(id, {
          message: errorMessage,
          variant: "error",
          duration: 4000,
        });

        throw error;
      }
    },
    [updateToast]
  );

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, showToast, promiseToast, dismissToast, updateToast }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
